import { useEffect } from "react";

export const SITE_URL = "https://buzzmobile.mk";
export const SITE_NAME = "Buzz Mobile";

const getAbsoluteUrl = (path = "/") => {
  if (path.startsWith("http")) {
    return path;
  }

  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

const setMetaTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes)
      .filter(([key]) => key === "name" || key === "property")
      .forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const setCanonical = (canonicalUrl) => {
  let element = document.head.querySelector("link[rel='canonical']");

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", canonicalUrl);
};

const setStructuredData = (structuredData) => {
  const existing = document.head.querySelector(
    "script[data-managed-schema='page']"
  );

  if (existing) {
    existing.remove();
  }

  if (!structuredData) {
    return;
  }

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-managed-schema", "page");
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

export const usePageSeo = ({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  structuredData,
}) => {
  useEffect(() => {
    const canonicalUrl = getAbsoluteUrl(canonicalPath);
    const imageUrl = image ? getAbsoluteUrl(image) : `${SITE_URL}/logo512.png`;

    document.documentElement.lang = "mk";
    document.title = title;

    setMetaTag("meta[name='description']", {
      name: "description",
      content: description,
    });
    setMetaTag("meta[property='og:title']", {
      property: "og:title",
      content: title,
    });
    setMetaTag("meta[property='og:description']", {
      property: "og:description",
      content: description,
    });
    setMetaTag("meta[property='og:type']", {
      property: "og:type",
      content: type,
    });
    setMetaTag("meta[property='og:url']", {
      property: "og:url",
      content: canonicalUrl,
    });
    setMetaTag("meta[property='og:image']", {
      property: "og:image",
      content: imageUrl,
    });
    setMetaTag("meta[name='twitter:card']", {
      name: "twitter:card",
      content: "summary_large_image",
    });
    setMetaTag("meta[name='twitter:title']", {
      name: "twitter:title",
      content: title,
    });
    setMetaTag("meta[name='twitter:description']", {
      name: "twitter:description",
      content: description,
    });
    setMetaTag("meta[name='twitter:image']", {
      name: "twitter:image",
      content: imageUrl,
    });

    setCanonical(canonicalUrl);
    setStructuredData(structuredData);
  }, [canonicalPath, description, image, structuredData, title, type]);
};

export const createBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: getAbsoluteUrl(item.path),
  })),
});

export const createArticleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.metaDescription,
  image: getAbsoluteUrl(post.heroImage),
  datePublished: post.datePublished || post.createdAt || post.updatedAt,
  dateModified: post.dateModified || post.updatedAt || post.datePublished,
  author: {
    "@type": "Organization",
    name: post.authorName || SITE_NAME,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/logo512.png`,
    },
  },
  mainEntityOfPage: getAbsoluteUrl(`/blog/${post.slug}`),
});

export const createBlogIndexSchema = (posts) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Buzz Mobile блог",
  itemListElement: posts.map((post, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: getAbsoluteUrl(`/blog/${post.slug}`),
    name: post.title,
  })),
});

export const createProductSchema = ({ product, productPath }) => {
  const prices = Array.isArray(product?.priceStorage)
    ? product.priceStorage
        .map((item) => Number(item?.price))
        .filter((price) => Number.isFinite(price) && price > 0)
    : [Number(product?.price)].filter(
        (price) => Number.isFinite(price) && price > 0
      );
  const offer =
    prices.length > 1
      ? {
          "@type": "AggregateOffer",
          priceCurrency: "MKD",
          lowPrice: Math.min(...prices),
          highPrice: Math.max(...prices),
          offerCount: prices.length,
          url: getAbsoluteUrl(productPath),
        }
      : prices.length === 1
      ? {
          "@type": "Offer",
          priceCurrency: "MKD",
          price: prices[0],
          url: getAbsoluteUrl(productPath),
        }
      : undefined;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: `${product.title} | ${SITE_NAME}`,
    image: (product.img || []).map((image) => getAbsoluteUrl(image)),
    sku: String(product._id || product.slug),
    ...(product.brand
      ? {
          brand: {
            "@type": "Brand",
            name: product.brand,
          },
        }
      : {}),
    ...(offer ? { offers: offer } : {}),
  };
};
